import type Model from '@models/base/base.model';
import type { ModelError } from '@models/base/base.model';
import type { ProcessStore } from '@stores/process.store';
import type { Collaborator, Permission } from '@stratumn/studio-shared';
import { workspaceActivationSchema } from '@stratumn/studio-shared';
import { computed, makeObservable, observable } from 'mobx';
import { z } from 'zod';
import { newError } from '@/services/errors/errors';
import { ProcessImageTag } from '@/types/process.types';

import { LocalDBModel } from './base/localDB.model';
import type { DatabaseModel } from './database.model';
import type { WorkflowModel } from './workflow.model';

const localStorageSchema = z.object({
  workflowIds: z.array(z.string())
});

export class ProcessModel extends LocalDBModel<
  z.infer<typeof localStorageSchema>
> {
  public name: string;
  public published: boolean;
  public permission: Permission;
  public collaborators: Collaborator[];
  public is_public: boolean;
  public updatedAt: string;
  public createdAt: string;
  public image: string;
  public draft: boolean;
  public icon =
    'https://storage.googleapis.com/speaknact_prod/users/avatars/avatar_1627425719_652.png';
  public deletedAt: Maybe<string>;
  public password: Maybe<string>;
  public gitlabRepositoryId: number;
  public organizationName: string;
  public tag: string | null;
  public workspaceActivated: boolean;

  public isSmallProcess: boolean;

  /* ------------------------ Store-related properties ------------------------ */
  public workflowIds: string[];
  public databaseIds: string[];
  public transitionIds: string[];
  public notificationTemplateIds: string[];

  constructor(
    store: ProcessStore,
    id: Model['id'],
    name: string,
    isSmall: boolean,
    icon: string,
    published: boolean,
    permission: Permission,
    collaborators: Collaborator[],
    is_public: boolean,
    updatedAt: string,
    createdAt: string,
    image: string,
    draft: boolean,
    gitlabRepositoryId: number,
    organizationName?: string,
    tag?: string | null,
    password?: string,
    deletedAt?: string,
    workspaceActivated?: boolean
  ) {
    super(store, id, localStorageSchema, false);

    this.name = name;
    this.icon = icon;
    this.published = published;
    this.permission = permission;
    this.collaborators = collaborators;
    this.is_public = is_public;
    this.updatedAt = updatedAt;
    this.createdAt = createdAt;
    this.image = image;
    this.draft = draft;
    this.gitlabRepositoryId = gitlabRepositoryId;
    this.organizationName = organizationName ?? '';
    this.tag = tag ?? null;
    this.password = password;
    this.deletedAt = deletedAt;
    this.workspaceActivated = workspaceActivated ?? false;
    this.isSmallProcess = isSmall;
    this.workflowIds = [];
    this.databaseIds = [];
    this.transitionIds = [];
    this.notificationTemplateIds = [];

    makeObservable(this, {
      name: observable,
      icon: observable,
      published: observable,
      permission: observable,
      collaborators: observable,
      is_public: observable,
      updatedAt: observable,
      image: observable,
      draft: observable,
      password: observable,
      deletedAt: observable,
      gitlabRepositoryId: observable,
      organizationName: observable,
      tag: observable,
      workspaceActivated: observable,
      isWorkspaceFeatureEnabled: computed,
      isProcessGenerable: computed,
      workflows: computed,
      hasGitlabRepo: computed
    });
  }

  /* ------------------------ Class properties setters ------------------------ */
  updateCollaborator(newCollaboratorId: string, permissions: Permission) {
    const foundCollaborator = this.collaborators.find(
      collaborator => collaborator.id === newCollaboratorId
    );
    if (!foundCollaborator) {
      return;
    }
    foundCollaborator.permissions = permissions;
  }

  setCollaborators(newCollaborators: Collaborator[]) {
    this.collaborators = newCollaborators;
  }

  setPassword(newPassword: string) {
    this.password = newPassword;
  }

  setIsPublic(newIsPublic: boolean) {
    this.is_public = newIsPublic;
  }

  setName(newName: string) {
    this.name = newName;
  }

  setImage(newImage: string) {
    this.image = newImage;
  }

  setIcon(newIcon: string) {
    this.icon = newIcon;
  }

  setGitlabRepositoryId(newId: number) {
    this.gitlabRepositoryId = newId;
  }

  setOrganizationName(newOrganizationName: string) {
    this.organizationName = newOrganizationName;
  }

  setTag(newTag: string | null) {
    this.tag = newTag;
  }

  setWorkspaceActivated(value: boolean) {
    this.workspaceActivated = value;
  }

  /* ----------------------------- Custom Getters ----------------------------- */
  get imageUrl() {
    return this.image?.split(':').slice(0, -1).join(':');
  }

  get imageTag() {
    const tag = this.image?.split(':').at(-1);
    if (!tag) {
      return;
    }
    if (!Object.values(ProcessImageTag).includes(tag as ProcessImageTag)) {
      return;
    }
    return tag as ProcessImageTag;
  }

  /**
   * Resolve workflow IDs into WorkflowModel instances. This getter is pure:
   * if a workflow ID does not resolve in the workflowStore (e.g., it has
   * been deleted but `workflowIds` was not yet pruned), it is silently
   * skipped. Cleanup of stale IDs is the explicit responsibility of
   * `removeWorkflowId` / workflow-deletion code paths — never of this
   * getter, which must remain side-effect-free per MobX `computed` rules.
   */
  get workflows(): WorkflowModel[] {
    const workflows: WorkflowModel[] = [];
    for (const workflowId of this.workflowIds) {
      const workflow = this.store.rootStore.workflowStore.get(workflowId);
      if (workflow) {
        workflows.push(workflow);
      }
    }
    return workflows;
  }

  get databases(): DatabaseModel[] {
    const databases: DatabaseModel[] = [];
    for (const databaseId of this.databaseIds) {
      const database = this.store.rootStore.databaseStore.get(databaseId);
      if (!database) {
        newError(
          'PROCM-pVx19',
          `Database ${databaseId} not found while getting databases for process ${this.id}, probably deleted`
        );
        continue;
      }
      databases.push(database);
    }
    return databases;
  }

  get isFullProcess(): boolean {
    return !this.isSmallProcess;
  }

  get hasGitlabRepo(): boolean {
    return this.gitlabRepositoryId > 0;
  }

  get isWorkspaceFeatureEnabled(): boolean {
    return workspaceActivationSchema.safeParse({
      gitlabRepositoryId: this.gitlabRepositoryId,
      organizationName: this.organizationName,
      workspaceActivated: this.workspaceActivated
    }).success;
  }

  get isProcessGenerable(): { generable: boolean; reasons: string[] } {
    if (this.workflows.length === 0) {
      return { generable: false, reasons: ['This process has no workflows'] };
    }

    if (this.draft) {
      const reasons: string[] = [];

      const missingEnvVarWorkflows = this.workflows.filter(
        workflow => !workflow.id_environment_variable_name
      );
      for (const wf of missingEnvVarWorkflows) {
        reasons.push(
          `Workflow "${wf.name}" is missing its ID Environment Variable Name`
        );
      }

      if (!this.image) {
        reasons.push('This process has no default Docker image configured');
      }

      if (reasons.length > 0) {
        return { generable: false, reasons };
      }
    } else if (!(this.image || (this.gitlabRepositoryId ?? 0) > 0)) {
      return {
        generable: false,
        reasons: [
          'This process has no Docker image or GitLab repository configured'
        ]
      };
    }

    return { generable: true, reasons: [] };
  }

  /* ----------------------------- Custom Setters ----------------------------- */
  set imageTag(newTag: ProcessImageTag | undefined) {
    if (!newTag) {
      return;
    }
    this.image = this.imageUrl?.concat(':').concat(newTag);
  }

  getLocalWorkflowIds() {
    return this.getLocal('workflowIds');
  }

  setWorkflowIds(newWorkflowIds: string[]) {
    this.workflowIds = newWorkflowIds;
    this.saveLocal('workflowIds', newWorkflowIds);
  }

  /**
   * Remove a workflow ID from this process. Used after a workflow is deleted
   * from the workflow store so the process's `workflowIds` does not retain
   * stale references. Idempotent.
   */
  removeWorkflowId(workflowId: string) {
    if (!this.workflowIds.includes(workflowId)) {
      return;
    }
    this.setWorkflowIds(this.workflowIds.filter(id => id !== workflowId));
  }

  setDatabaseIds(newDatabaseIds: string[]) {
    this.databaseIds = newDatabaseIds;
  }

  setTransitionIds(newTransitionIds: string[]) {
    this.transitionIds = newTransitionIds;
  }

  setNotificationTemplateIds(newNotificationTemplateIds: string[]) {
    this.notificationTemplateIds = newNotificationTemplateIds;
  }

  public static getLocalWorkflowIds(processId: Maybe<string>) {
    return ProcessModel.getLocal<string[]>(
      'ProcessModel',
      processId,
      'workflowIds'
    );
  }

  /* ----------------------------- Abstract methods ----------------------------- */
  get toJSON() {
    return {
      name: this.name,
      is_public: this.is_public,
      password: this.password,
      image: this.image
    };
  }

  get errors(): ModelError[] {
    return [];
  }
}
